import { useState } from 'react'
import { useLayoutsQuery } from '@/Views/FormSteps/Layout/LayoutInput.generated'
import throwGQLError from '@/Utilities/Urql/Errors/throwGQLError'

interface LayoutSectionProps {
  eventTypeCollection?: string
  initialLayout?: string
  onChange: (layout: string | undefined) => void
}

type SetupChoice = 'not_sure' | 'standing' | 'seating'

const deriveSetupChoice = (layout: string | undefined): SetupChoice | undefined => {
  if (!layout) return undefined
  if (layout === 'not_sure') return 'not_sure'
  if (layout === 'standing') return 'standing'
  return 'seating'
}

const formatSeatingLabel = (name: string) => {
  if (name === 'seating') return 'No preference'
  return name.charAt(0).toUpperCase() + name.slice(1)
}

const TOP_LEVEL_OPTIONS: { value: SetupChoice; label: string }[] = [
  { value: 'not_sure', label: 'Not sure' },
  { value: 'standing', label: 'Standing' },
  { value: 'seating', label: 'Seating' },
]

const LayoutSection = ({ eventTypeCollection, initialLayout, onChange }: LayoutSectionProps) => {
  const [setupChoice, setSetupChoice] = useState<SetupChoice | undefined>(
    deriveSetupChoice(initialLayout)
  )
  const [seatingLayout, setSeatingLayout] = useState<string | undefined>(
    initialLayout && initialLayout !== 'not_sure' && initialLayout !== 'standing'
      ? initialLayout
      : undefined
  )

  const [{ error, data }] = useLayoutsQuery({
    pause: !eventTypeCollection,
    requestPolicy: 'cache-first',
    variables: { id: eventTypeCollection ?? '' },
  })

  if (error) throwGQLError(error)

  const rawLayouts = data?.collection?.layouts ?? []
  const seatingLayouts = [
    ...rawLayouts.filter((l) => l !== 'standing' && l !== 'seating'),
    'seating',
  ]

  const handleTopLevelChange = (choice: SetupChoice) => {
    setSetupChoice(choice)
    if (choice === 'not_sure') {
      onChange('not_sure')
    } else if (choice === 'standing') {
      onChange('standing')
    } else {
      setSeatingLayout(undefined)
      onChange(undefined)
    }
  }

  const handleSeatingLayoutChange = (value: string) => {
    setSeatingLayout(value)
    onChange(value)
  }

  return (
    <div className='flex flex-col gap-4'>
      <p className='!font-bold text-sm sm:text-base text-black leading-6'>
        How would you like the space set up? <span className='text-[#af231c]'>*</span>
      </p>
      {!eventTypeCollection ? (
        <p className='text-sm text-[#8c8c8c] leading-5'>
          Select an event type above to see layout options.
        </p>
      ) : (
        <>
          <div className='flex flex-col gap-3'>
            {TOP_LEVEL_OPTIONS.map(({ value, label }) => (
              <label key={value} className='flex items-center gap-2 cursor-pointer select-none'>
                <input
                  type='radio'
                  name='duplicate-setup-choice'
                  value={value}
                  checked={setupChoice === value}
                  onChange={() => handleTopLevelChange(value)}
                  className='w-5 h-5 cursor-pointer accent-black shrink-0'
                />
                <span className='text-sm sm:text-base text-[#1f1f1f] leading-6'>{label}</span>
              </label>
            ))}
          </div>

          {setupChoice === 'seating' && (
            <div className='flex flex-col gap-3 pl-1'>
              <p className='!font-bold text-sm sm:text-base text-black leading-6'>
                Please choose a seating layout
              </p>
              <div className='grid grid-flow-col grid-rows-2 gap-x-2 sm:gap-x-4 gap-y-3 w-full'>
                {seatingLayouts.map((value) => (
                  <label key={value} className='flex items-center gap-2 cursor-pointer select-none'>
                    <input
                      type='radio'
                      name='duplicate-seating-layout'
                      value={value}
                      checked={seatingLayout === value}
                      onChange={(e) => handleSeatingLayoutChange(e.target.value)}
                      className='w-5 h-5 cursor-pointer accent-black shrink-0'
                    />
                    <span className='text-sm sm:text-base text-[#1f1f1f] leading-6'>
                      {formatSeatingLabel(value)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default LayoutSection
